import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-raspberry-800"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-emerald-700" />
          </div>
          <p className="text-xs font-bold tracking-widest uppercase text-raspberry-800">Privacy Policy</p>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-3">
          Gynovate AI/SI – Privacy Policy
        </h1>
        <p className="text-xs text-slate-500 mb-10">Effective Date: 1 March 2026</p>

        <div className="prose prose-slate max-w-none text-sm sm:text-base leading-relaxed text-slate-700 space-y-6">
          <p>Gynovate AI/SI is committed to protecting your privacy and ensuring secure handling of data.</p>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">1. Information We Collect</h2>
            <p>We may collect:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Name, email, phone number</li>
              <li>Professional details (specialty, qualification)</li>
              <li>Usage data related to platform interaction</li>
            </ul>
            <p className="mt-2">We recommend users avoid entering identifiable patient information unless necessary and permitted.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">2. Use of Information</h2>
            <p>We use collected data to:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Provide and improve our services</li>
              <li>Generate AI-based outputs</li>
              <li>Enhance user experience</li>
              <li>Ensure platform security</li>
            </ul>
            <p className="mt-2 font-semibold text-slate-900">We do not sell personal data.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">3. Data Protection</h2>
            <p>We implement appropriate technical and organizational measures, including:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Encryption of data</li>
              <li>Secure servers</li>
              <li>Restricted access controls</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">4. Data Sharing</h2>
            <p>We do not share personal data with third parties except:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>When required by law</li>
              <li>To comply with legal obligations</li>
              <li>To protect platform integrity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">5. User Responsibility</h2>
            <p>Users are responsible for:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Ensuring lawful use of any patient data</li>
              <li>Obtaining necessary consent before entering sensitive data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">6. Data Retention and Deletion</h2>
            <p>We retain data only as long as necessary for service delivery.</p>
            <p className="mt-2">Users may request deletion of their data by contacting us at:{' '}
              <a href="mailto:theyellowtribegoa@gmail.com" className="text-raspberry-800 font-semibold hover:underline">
                theyellowtribegoa@gmail.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">7. User Rights</h2>
            <p>In accordance with the Digital Personal Data Protection Act, users have the right to:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Access their data</li>
              <li>Request correction</li>
              <li>Request deletion</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">8. Updates to Policy</h2>
            <p>We may update this policy from time to time. Continued use of the platform constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">9. Contact</h2>
            <p>For any privacy-related concerns, contact:{' '}
              <a href="mailto:theyellowtribegoa@gmail.com" className="text-raspberry-800 font-semibold hover:underline">
                theyellowtribegoa@gmail.com
              </a>
            </p>
          </section>

          <div className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap gap-3">
            <Link to="/disclaimer" className="text-raspberry-800 font-semibold hover:underline">Medical Disclaimer →</Link>
            <span>·</span>
            <Link to="/" className="text-raspberry-800 font-semibold hover:underline">Back to Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
